import { PieceFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { extractComponentFromChunk } from '@/ui/utils/tiles';

/**
 * Last resort for the player's accessible name. It stays untranslated on purpose: `alt-text` is a
 * required field on the `video-seo` piece, so this only ever surfaces for incomplete content.
 */
export const DEFAULT_VIDEO_ARIA_LABEL = 'Video';

export interface VideoSeo {
  name?: string;
  description?: string;
  uploadDate?: string;
  altText?: string;
}

/**
 * Reads the reusable `video-seo` piece. Every field is optional here — the JSON-LD builder
 * decides which combinations are good enough to emit.
 */
export function parseVideoSeo(piece: PieceFragment | null | undefined): VideoSeo {
  const components = piece?.components;
  if (!components) {
    return {};
  }

  // `datetime` is typed as `any` by the GraphQL codegen, so it is narrowed before use.
  const rawUploadDate: unknown = extractComponentFromChunk(
    components,
    'upload-date',
    'DatetimeContent',
  )?.datetime;

  return {
    name: extractComponentFromChunk(components, 'name', 'SingleLineContent')?.text ?? undefined,
    description:
      extractComponentFromChunk(components, 'description', 'SingleLineContent')?.text ?? undefined,
    uploadDate: typeof rawUploadDate === 'string' ? rawUploadDate : undefined,
    altText:
      extractComponentFromChunk(components, 'alt-text', 'SingleLineContent')?.text ?? undefined,
  };
}

/**
 * The accessible name of a video player: the editorial alt text first, then the video name, then
 * whatever the tile can offer (poster alt text, headline, overlay copy).
 */
export function getVideoAriaLabel(
  seo: VideoSeo | null | undefined,
  ...fallbacks: Array<string | null | undefined>
): string {
  const candidates = [seo?.altText, seo?.name, ...fallbacks];
  const label = candidates.find((candidate) => candidate?.trim());

  return label?.trim() ?? DEFAULT_VIDEO_ARIA_LABEL;
}
